import { useState } from 'react';
import Modal from './Modal';
import ConfirmModal from './ConfirmModal';
import UserAvatar from './UserAvatar';
import FormField from './shared/FormField';
import api from '../api';
import { DeleteIcon, PlusIcon, MinusIcon } from './Icons';
import { PROJECT_TYPE_CONFIG } from '../constants';

export default function ProjectSettingsModal({ project, onClose, onProjectUpdated, onProjectDeleted, users, showToast }) {
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description || '');
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const members = users.filter((u) => project.members?.includes(u.uid));
  const nonMembers = users.filter((u) => !project.members?.includes(u.uid));

  const handleSave = async () => {
    if (!name.trim() || saving) return;
    setSaving(true);
    try {
      const updated = await api.updateProject(project.id, {
        name: name.trim(),
        description: description.trim(),
      });
      onProjectUpdated(updated);
      showToast('Project updated', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to update project', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddMember = async (uid) => {
    try {
      const updated = await api.addProjectMember(project.id, uid);
      onProjectUpdated(updated);
      showToast('Member added', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to add member', 'error');
    }
  };

  const handleRemoveMember = async (uid) => {
    try {
      const updated = await api.removeProjectMember(project.id, uid);
      onProjectUpdated(updated);
      showToast('Member removed', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to remove member', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await api.deleteProject(project.id);
      onProjectDeleted();
      showToast('Project deleted', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to delete project', 'error');
    }
  };

  return (
    <Modal
      title="Project Settings"
      onClose={onClose}
      footer={
        <div className="modal-footer-actions">
          <button className="btn btn-danger btn-sm" onClick={() => setShowDeleteConfirm(true)}>
            <DeleteIcon size={14} /> Delete Project
          </button>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={!name.trim() || saving}>
            Save Changes
          </button>
        </div>
      }
    >
      <div className="add-item-form">
        <FormField label="Project Name">
          <input
            className="form-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </FormField>
        <FormField label="Description">
          <textarea
            className="form-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />
        </FormField>
        <FormField label="Board Type">
          <div className="form-input-readonly">
            {PROJECT_TYPE_CONFIG[project.type || 'scrum']?.label || 'Scrum'}
          </div>
        </FormField>
        <FormField label={`Members (${members.length})`}>
          <div className="project-settings-members">
            {members.map((u) => (
              <div key={u.uid} className="project-settings-member-row">
                <UserAvatar user={u} size={32} showStatus={false} />
                <span className="project-settings-member-name">{u.displayName}</span>
                {u.uid !== project.createdBy && (
                  <button className="btn-icon" onClick={() => handleRemoveMember(u.uid)} title="Remove member">
                    <MinusIcon size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </FormField>
        {nonMembers.length > 0 && (
          <FormField label="Add Members">
            <div className="project-settings-members">
              {nonMembers.map((u) => (
                <div key={u.uid} className="project-settings-member-row">
                  <UserAvatar user={u} size={32} showStatus={false} />
                  <span className="project-settings-member-name">{u.displayName}</span>
                  <button className="btn-icon" onClick={() => handleAddMember(u.uid)} title="Add member">
                    <PlusIcon size={14} />
                  </button>
                </div>
              ))}
            </div>
          </FormField>
        )}
      </div>

      {showDeleteConfirm && (
        <ConfirmModal
          message="Delete this project and ALL its sprints and items? This cannot be undone."
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
          confirmLabel="Delete Project"
        />
      )}
    </Modal>
  );
}
